import React, { useState, useEffect } from 'react';
import { useWarehouseContext } from './warehouse-context';
import { UseFormReturn, FormProvider } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useService } from '../../../../services/services';
import GlobalModal from '../../../../components/modals/modal';
import {
  ButtonComponent,
  ButtonStyling,
} from '../../../../components/form/elements/button';
import css from '../index.module.css';
import { useFieldArray, useWatch } from 'react-hook-form';
import { DecimalComponent } from '../../../../components/form/elements/decimal';
import { SelectComponent } from '../../../../components/form/elements/select';
import { map, tap } from 'rxjs/operators';
import Paginate from '../../../../interfaces/paginate';
import Warehouse from '../../../../interfaces/warehouse';
import DealType from '../../../../enums/deal-type';
import TextButton from '../../../../components/text-button/text-button';

const UUID_LENGTH = 36;
interface PartyNumber {
  party_number: string;
}

const WarehouseModal = ({
  formMethods,
}: {
  formMethods: UseFormReturn;
  type?: string;
  keyValue?: string;
}) => {
  const { isWarehouseModalOpen, setIsWarehouseModalOpen } =
    useWarehouseContext();
  const { t } = useTranslation();
  const { dealType } = useParams<{
    dealType: DealType;
  }>();

  const { control, setValue } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'product_warehouses',
  });

  const allAmounts = useWatch({
    name: fields.map((_, index) => `product_warehouses.${index}.amount`),
    control,
  });

  const [initialState, setInitialState] = useState<any>([]);

  useEffect(() => {
    const parsed = allAmounts
      .map((val) => parseFloat(val))
      .filter((val) => !isNaN(val));

    setValue(
      'total_amount',
      parsed.reduce((acc, curr) => acc + curr, 0)
    );
  }, [allAmounts, setValue]);

  useEffect(() => {
    if (isWarehouseModalOpen === true) {
      setInitialState(formMethods.getValues('product_warehouses'));
    }
  }, [isWarehouseModalOpen, setInitialState, formMethods]);

  const { warehouseService, dealService } = useService();

  const { farmId } = useParams<{
    farmId: string;
  }>();

  const onClose = () => {
    formMethods.setValue('product_warehouses', initialState);
    setIsWarehouseModalOpen(false);
  };

  const onSave = () => {
    const amount = formMethods.getValues('amount');
    const totalAmount = formMethods.getValues('total_amount');

    if (parseFloat(amount) === parseFloat(totalAmount)) {
      setIsWarehouseModalOpen(false);
    }
  };

  const body = (
    <FormProvider {...formMethods}>
      <form onSubmit={() => setIsWarehouseModalOpen(false)} autoComplete="off">
        {fields.map((_, index) => {
          return (
            <>
              <div className={css['modal_form_entry']}>
                <SelectComponent
                  name={`product_warehouses.${index}.warehouse_id`}
                  label={'pages.warehouse.deals.warehouse'}
                  horizontal={true}
                  asyncOptions={(value, _, { page, getValues, setValue }) => {
                    return warehouseService
                      .index(farmId, {
                        page,
                        name: value,
                        product: getValues('product_id.value'),
                      })
                      .pipe(
                        tap((response: Paginate<Warehouse>) => {
                          if (response.data.length === 1) {
                            setValue(
                              `product_warehouses.${index}.warehouse_id`,
                              {
                                label: response.data[0].name,
                                value: response.data[0].id,
                              }
                            );
                          }
                        }),
                        map((response: Paginate<Warehouse>) => ({
                          hasMore: response.total === response.per_page,
                          options: response.data.map((v) => ({
                            label: v.name,
                            value: v.id,
                          })),
                          additional: { page: response.current_page + 1 },
                        }))
                      );
                  }}
                />
                <SelectComponent
                  name={`product_warehouses.${index}.party_number`}
                  label={'pages.warehouse.deals.party_number'}
                  horizontal={true}
                  cacheOn={[`product_warehouses.${index}.warehouse_id`]}
                  asyncOptions={(value, _, { page, getValues, setValue }) => {
                    const dealProductId = getValues('id') ?? '';
                    const correctUuidLength =
                      dealProductId.length === UUID_LENGTH;

                    if (
                      getValues(`product_warehouses.${index}.warehouse_id`)
                        ?.value
                    ) {
                      return dealService
                        .partyNumbers(farmId, {
                          page,
                          name: value,
                          product: getValues('product_id.value'),
                          direction: 'incoming',
                          exclude_deal_product_ids: [
                            correctUuidLength ? dealProductId : null,
                          ].filter(Boolean),
                          warehouse_id: getValues(
                            `product_warehouses.${index}.warehouse_id`
                          )?.value,
                        })
                        .pipe(
                          tap((response: Paginate<PartyNumber>) => {
                            switch (response.data.length) {
                              case 0:
                                setValue(
                                  `product_warehouses.${index}.party_number`,
                                  null
                                );
                                break;
                              case 1:
                                setValue(
                                  `product_warehouses.${index}.party_number`,
                                  {
                                    label: response.data[0].party_number,
                                    value: response.data[0].party_number,
                                  }
                                );
                                break;
                            }
                          }),
                          map((response: Paginate<PartyNumber>) => ({
                            hasMore: response.total === response.per_page,
                            options: response.data.map((v) => ({
                              label: v.party_number,
                              value: v.party_number,
                            })),
                            additional: { page: response.current_page + 1 },
                          }))
                        );
                    }

                    return new Promise(null);
                  }}
                />
                <DecimalComponent
                  name={`product_warehouses.${index}.amount`}
                  label={`pages.warehouse.deals.event.${dealType}`}
                  horizontal
                  icon={null}
                  negative={true}
                  requiredShow={false}
                />
                <div className={css['modal_form_delete']}>
                  <button
                    type="button"
                    onClick={() => {
                      remove(index);
                    }}
                  >
                    &#10006;
                  </button>
                </div>
              </div>

              <hr className={css['modal_form_entry_line']} />
            </>
          );
        })}
        <div className={css['modal_end']}>
          <div className={css['add_warehouse_button']}>
            <TextButton
              name={t('pages.warehouse.deals.add_warehouse')}
              onClick={() => append({})}
            />
          </div>
          <div className={css['modal_end_of_line']}>
            <div className={css['modal_end_of_line_input']}>
              <DecimalComponent
                name={`total_amount`}
                label={`pages.warehouse.deals.event.${dealType}_total_amount`}
                horizontal
                disabled
                icon={null}
                requiredShow={false}
                negative={true}
              />
            </div>
          </div>
          <div className={css['modal_end_of_line']}>
            <div className={css['modal_end_of_line_input']}>
              <DecimalComponent
                requiredShow={false}
                name={`amount`}
                label={'pages.warehouse.deals.amount'}
                horizontal
                disabled
                icon={null}
                negative={true}
              />
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );

  return (
    <GlobalModal
      visible={isWarehouseModalOpen}
      onClose={() => setIsWarehouseModalOpen(false)}
      title={t('components.sidebar.warehouse.title')}
      body={body}
      className={css['modal']}
      buttons={
        <>
          <ButtonComponent
            name="submit"
            click={() => onSave()}
            label={t('save')}
            ignoreValidation
          />
          <ButtonComponent
            name="cancel"
            click={onClose}
            label={t('cancel')}
            styling={ButtonStyling.cancel}
            ignoreValidation
          />
        </>
      }
    />
  );
};

export default WarehouseModal;
